import { inject, Injectable, OnDestroy } from "@angular/core";
import { ActivatedRoute, ParamMap, QueryParamsHandling, Router } from "@angular/router";
import { Store } from "@ngxs/store";
import { CaseData, CaseUser, RealTimeNotification, User, UserTypeFriendlyId } from "@vp/models";
import { EMPTY, Observable, Subject } from "rxjs";
import { exhaustMap, filter, pairwise, takeUntil, withLatestFrom } from "rxjs/operators";
import { CaseDataFilter } from "../models/case-data-filter";
import * as CaseFilterStateActions from "../state+/case-filter-state.actions";

import { ApplicationState } from "@vp/data-access/application";
import { filterNullMap } from "@vp/shared/operators";
import * as SignalRStateActions from "@vp/shared/signal-r-service";
import { CaseFilterState } from "../state+/case-filter.state";

@Injectable()
export class CaseFilterStateNavigationService implements OnDestroy {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly store = inject(Store);

  filteredCaseData$: Observable<CaseData[]> = inject(Store).select(CaseFilterState.results);
  currentFilter$: Observable<Partial<CaseDataFilter>> = inject(Store).select(
    CaseFilterState.currentFilter
  );
  loggedInUser$: Observable<User | null> = inject(Store).select(ApplicationState.loggedInUser);

  private _destroyed$ = new Subject<void>();

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  /**
   * Keep the URL in sync with `CaseFilterState`.
   *
   * `queryParamsHandling` defaults to "" — REPLACE — so the filter's params
   * become the whole query string. A surface that keeps its own params in the
   * URL beside the filter's (the vCI queue's `tab`/`topic`) must pass "merge",
   * or every filter emission silently drops them: the reading component still
   * restores from the params it read on init, so the view looks right while the
   * URL no longer says why, and a reload or a shared link loses the view.
   */
  listen(resetStatusOnSearch = true, queryParamsHandling: QueryParamsHandling = "") {
    this.activatedRoute.queryParamMap
      .pipe(
        takeUntil(this._destroyed$),
        exhaustMap((paramMap: ParamMap) => {
          const search: string | null = paramMap.get("search");
          const currentFilter = this.store.selectSnapshot(CaseFilterState.currentFilter);
          if (currentFilter.search !== search && Object.keys(currentFilter).length > 0) {
            return this.store.dispatch(
              new CaseFilterStateActions.SetFilterState({
                ...currentFilter,
                search: search,
                status: resetStatusOnSearch ? [] : currentFilter.status
              })
            );
          }
          return EMPTY;
        })
      )
      .subscribe();

    this.currentFilter$
      .pipe(
        takeUntil(this._destroyed$),
        filter(filter => Object.keys(filter).length > 0)
      )
      .subscribe((filter: Partial<CaseDataFilter>) => {
        this.navigate(filter, queryParamsHandling);
      });

    this.filteredCaseData$
      .pipe(
        pairwise(),
        withLatestFrom(this.loggedInUser$.pipe(filterNullMap())),
        takeUntil(this._destroyed$)
      )
      .subscribe({
        next: ([[previous, current], user]: [[CaseData[], CaseData[]], User]) => {
          this.store.dispatch(
            new SignalRStateActions.ReconcileGroups(
              getNotifications(previous, user),
              getNotifications(current, user)
            )
          );
        }
      });
  }

  navigate(
    searchParams: Partial<CaseDataFilter>,
    queryParamsHandling: QueryParamsHandling | null = "",
    skipLocationChange = false,
    replaceUrl = true
  ): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: searchParams,
      queryParamsHandling,
      skipLocationChange,
      replaceUrl
    });
  }
}

const getNotifications = (caseData: CaseData[], user: User) => {
  return caseData.reduce((acc: RealTimeNotification[], caseData: CaseData) => {
    const deviceUser = caseData.users.find(
      (caseUser: CaseUser | null) => caseUser?.userType === UserTypeFriendlyId.device
    );
    if (deviceUser) {
      acc.push({
        userId: user.userId,
        groupName: deviceUser.userId
      } as RealTimeNotification);
    }
    acc.push({
      userId: user.userId,
      groupName: caseData.caseId
    } as RealTimeNotification);
    return acc;
  }, []);
};
